import {
  Control,
  Controller,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
} from 'react-hook-form';

import InputMask from '@mona-health/react-input-mask';
import React from 'react';
import classNames from 'classnames';

export interface InputProps {
  defaultValue?: string | boolean;
  isDisabled?: boolean;
  name: string;
  type?: string;
  register?: UseFormRegister<FieldValues | any>;
  placeholder?: string;
  validations?: RegisterOptions<FieldValues, string>;
  label?: string;
  suffix?: JSX.Element;
  prefix?: JSX.Element;
  className?: string;
  error?: string;
  mask?: string;
  control?: Control<FieldValues>;
  dataTestId?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  beforeMaskedValueChange?: (newState: {
    nextState: {
      value: string;
      selection: { start: number; end: number };
      enteredString?: string;
    };
  }) => {
    value: string;
    selection: { start: number; end: number };
  };
}
export default function Input({
  defaultValue,
  isDisabled,
  suffix,
  type,
  register,
  placeholder,
  validations,
  label,
  prefix,
  name,
  className,
  error,
  mask,
  control,
  dataTestId,
  onBlur,
  onChange,
  beforeMaskedValueChange,
}: InputProps) {
  return (
    <div
      className={classNames('input', className, {
        'input--disabled': isDisabled,
      })}
    >
      <div className="input__label">
        {label && <label htmlFor={name}>{label}</label>}
        {placeholder && <div className="input__placeholder d-none d-md-block">{placeholder}</div>}
      </div>
      {prefix && <div className="input__prefix">{prefix}</div>}
      <div className="input__container">
        <div
          className={classNames('input__field', {
            'input__field--error': !!error,
            'input__field--compact': type !== 'checkbox',
            'input__field--large': type === 'checkbox',
          })}
        >
          {mask && type !== 'checkbox' && control ? (
            <Controller
              name={name}
              control={control}
              defaultValue={defaultValue ?? ''}
              rules={validations}
              render={({ field }) => (
                <InputMask
                  id={name}
                  name={name}
                  alwaysShowMask={true}
                  mask={mask}
                  maskPlaceholder="-"
                  value={field.value ?? defaultValue}
                  onChange={field.onChange}
                  onBlur={onBlur}
                  beforeMaskedStateChange={beforeMaskedValueChange}
                  data-test-id={dataTestId}
                />
              )}
            />
          ) : type !== 'checkbox' ? (
            <input
              type={type}
              defaultValue={typeof defaultValue === 'string' ? defaultValue : undefined}
              {...register!(name, { ...validations })}
              name={name}
              disabled={isDisabled}
              onBlur={onBlur}
              data-testid={dataTestId}
              id={name}
            />
          ) : (
            <input
              type={type}
              defaultChecked={typeof defaultValue === 'boolean' ? defaultValue : undefined}
              {...register!(name, { ...validations })}
              name={name}
              disabled={isDisabled}
              onBlur={onBlur}
              {...(onChange && { onChange })}
              id={name}
              data-testid={dataTestId}
            ></input>
          )}
          {suffix && <div className="input__suffix">{suffix}</div>}
        </div>

        {placeholder && <div className="input__placeholder d-block d-md-none">{placeholder}</div>}
        {error && (
          <span data-test-id={`${dataTestId}-error`} className="input__error">
            {error}
          </span>
        )}
      </div>
    </div>
  );
}
